import { MissingDataSource } from 'components/helpers/EditingHelpText';
import Container from 'components/helpers/Container';
import { TcDirectoryListingProps } from './_types';
import { classnames } from 'tailwindcss-classnames';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { TcDirectoryListingClasses } from 'components/authorable/NmdpSite/TcDirectoryListing/_classes';

const GetOutsideIntervalBlock = (outsideInterval: number) => {
  switch (outsideInterval) {
    case -1:
      return (
        <div className={TcDirectoryListingClasses.Paragraph}>
          This center&apos;s <b>actual 1-year survival</b> results are <b>below</b> the{' '}
          <b>expected rate</b> for this center*.
        </div>
      );
    case 0:
      return (
        <div className={TcDirectoryListingClasses.Paragraph}>
          This center&apos;s <b>actual 1-year survival</b> results are <b>similar</b> to the{' '}
          <b>expected rate</b> for this center*.
        </div>
      );
    case 1:
      return (
        <div className={TcDirectoryListingClasses.Paragraph}>
          This center&apos;s <b>actual 1-year survival</b> results are <b>above</b> the{' '}
          <b>expected rate</b> for this center*.
        </div>
      );
    default:
      return (
        <div className={TcDirectoryListingClasses.Paragraph}>
          Survival analysis data is not available for this center.
        </div>
      );
  }
};

const OverAllSurvivalTab = ({ fields }: TcDirectoryListingProps): React.JSX.Element => {
  // Fail out if we don't have any fields
  if (!fields?.survivalAnalysis) {
    return <MissingDataSource />;
  }
  const showCenterName = fields.survivalAnalysis.length > 1;

  const survivorYears = fields?.listing?.survivorYears
    .map((survivorYear) => (survivorYear.fields as { Value: { value: string } }).Value?.value)
    .join(', ');

  const noSurvivorDataYear = (
    fields?.listing?.noSurvivorDataYear?.fields as { Value: { value: string } }
  )?.Value?.value;

  const linkField = {
    value: {
      href: 'https://bloodstemcell.hrsa.gov/data',
      text: 'U.S. Patient Survival report',
      linktype: 'external',
      target: '_blank',
    },
  };
  const maxLength = fields.survivalAnalysis.length - 1;

  return (
    <Container size="standard" dataComponent="authorable/nmdpsite/tcdirectory/overallsurvival">
      {fields.survivalAnalysis.length > 0 ? (
        <>
          <h2 className={TcDirectoryListingClasses.Heading.Medium}>
            Patient survival information for this center
          </h2>
          {fields.survivalAnalysis.map((x, i) => (
            <div key={x.id}>
              {x?.fields?.dataIsNotSufficient?.value ? (
                <>
                  {x?.displayName && <h4 className={classnames('heading-sm')}>{x?.displayName}</h4>}
                  <div>
                    {x?.displayName} data is not sufficient to develop survival estimates for{' '}
                    {noSurvivorDataYear}.
                  </div>
                </>
              ) : (
                <>
                  {showCenterName && x.displayName && (
                    <h4 className={classnames('heading-sm')}>{showCenterName && x.displayName}</h4>
                  )}
                  {GetOutsideIntervalBlock(x?.fields?.outsideInterval?.value)}
                  <div className={TcDirectoryListingClasses.Paragraph}>
                    The survival information we have for this center <b>includes ONLY:</b>
                  </div>
                  <ol className={classnames('list-decimal', 'list-inside', 'pb-2')}>
                    <li>
                      Patients who had their <b>FIRST ALLOGENEIC</b> transplant (cells from a
                      related or unrelated donor/cord blood) during {survivorYears} <b>and</b>
                    </li>
                    <li>
                      Who had their transplant at a U.S. transplant center, <b>and</b>
                    </li>
                    <li>
                      Who had follow-up information provided by the transplant center for analysis
                    </li>
                  </ol>
                  <div className={TcDirectoryListingClasses.Paragraph}>
                    For this center, we have survival information for{' '}
                    <b>{x?.fields?.cases?.value}</b>{' '}
                    {x?.fields?.cases?.value > 1 ? 'patients' : 'patient'}.
                  </div>
                  <div className={TcDirectoryListingClasses.Paragraph}>
                    The <b>actual 1-year survival</b> of{' '}
                    {x?.fields?.cases?.value > 1 ? 'these patients' : 'this patient'} is{' '}
                    <b>{Math.round((x?.fields?.actual?.value as number) * 10) / 10}%</b>.
                  </div>
                  <div className={TcDirectoryListingClasses.Paragraph}>
                    Compared to similar patients transplanted at all centers in the U.S., we expect
                    that the 1-year survival for patients at this center to be in a range{' '}
                    <b>
                      between {Math.round((x?.fields?.confidenceLower?.value as number) * 10) / 10}%
                      and {Math.round((x?.fields?.confidenceUpper?.value as number) * 10) / 10}%.
                    </b>
                  </div>
                  <div className={TcDirectoryListingClasses.Paragraph}>
                    For help with understanding these statistics, please see{' '}
                    <LinkA11yWrapper
                      className={classnames('text-blue-morning', 'underline')}
                      field={fields?.listing?.treatmentDecisions}
                    />
                    .
                  </div>
                  <div className={TcDirectoryListingClasses.Paragraph}>
                    For overall survival for all patients transplanted with a specific disease,
                    please see{' '}
                    <LinkA11yWrapper
                      className={classnames('text-blue-morning', 'underline')}
                      field={linkField}
                    />{' '}
                    at bloodcell.transplant.hrsa.gov.
                  </div>
                  {i < maxLength && <hr className={classnames('my-8', 'lg:my-12')} />}
                </>
              )}
            </div>
          ))}
        </>
      ) : (
        <>
          <p>
            This center&apos;s data is not sufficient to develop survival estimates for{' '}
            {noSurvivorDataYear}.
          </p>
          <p>
            If you have questions, contact Be The Match Patient Support Center at{' '}
            <a href="tel:18889996743">1 (888) 999-6743</a> or{' '}
            <a href="mailto:patientinfo@nmdp.org">patientinfo@nmdp.org</a>.
          </p>
        </>
      )}
      {fields.survivalAnalysis.length > 0 && (
        <>
          <p className={classnames('pb-2', 'text-gray-darkGray', 'text-sm', 'pt-2')}>
            * The expected survival rate for a transplant center can be below, similar to, or above
            the range listed. This is based on comparing patient survival at all centers that
            treated patients with similar diseases.
          </p>
          <p className={classnames('text-gray-darkGray', 'text-sm')}>
            The survival rate cannot tell how you will do with your transplant. Talk to your doctor
            to understand your prognosis or the likely course of your disease.
          </p>
        </>
      )}
    </Container>
  );
};

export default OverAllSurvivalTab;
